import React, { useRef } from 'react';

import { SectionTitle } from '@components/admin/SectionTitle';
import { Button } from '@components/Button';
import { Stepper } from '@components/Stepper';
import { steps } from '@data/bookingInquiry';
import { useSearch } from '@hooks/useSearch';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import * as t from 'io-ts';
import { useHistory } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: '予約申込',
    dashboardTitle: '予約作成',
    thanksTitle: 'ありがとうございました！',
    backText: 'TOPへ戻る',
    goToInquiry: '予約一覧へ進む',
    article: (d2d_no?: string, id?: string, is_dashboard?: boolean) => (
      <>
        予約を受付いたしました。
        <br />
        {d2d_no && (
          <>
            本件のD2D番号：
            {is_dashboard ? (
              <a href={`/admin/booking/InquiryDetails?d2dNo=${d2d_no}`}>{d2d_no}</a>
            ) : (
              <span style={{ display: 'inline-block' }}>{d2d_no}</span>
            )}
            <br />
          </>
        )}
        {!is_dashboard && (
          <>
            <br />
            予約の手配を進めます。
            <br />
            予約の状況は「
            <a href="/utilities/Bookings/BookingInquiryHistory">最新予約</a>
            」の画面にてご確認いただけます。
          </>
        )}
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  articleContainer: {
    ...common.flexColumn,
    ...common.flexCenter,
    padding: '4rem',
    backgroundColor: theme.palette.background.paper,
  },
  button: {
    marginTop: 54,
    width: 200,
    margin: '0 20px',
    position: 'relative',
    borderRadius: 4,
    '&$next:before': {
      content: '""',
      position: 'absolute',
      width: 6,
      height: 6,
      top: 'auto',
      right: 10,
      botton: 'auto',
      border: '6px solid transparent',
      borderLeft: `6px solid ${theme.palette.common.white}`,
    },
    '&$prev:before': {
      content: '""',
      position: 'absolute',
      width: 6,
      height: 6,
      top: 'auto',
      left: 10,
      botton: 'auto',
      border: '6px solid transparent',
      borderRight: `6px solid ${theme.palette.common.white}`,
    },
  },
  thanksTitle: {
    ...common.normalText,
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: 24,
    padding: '20px 0',
  },
  article: {
    padding: '20px 0',
    ...common.normalText,
    textAlign: 'center',
    color: theme.palette.grey[500],
    '& a': {
      ...common.textPrimary,
      textDecoration: 'underline',
    },
  },
}));

export interface BookingCreateFormCompleteProps {
  isDashboard?: boolean;
}

export const BookingCreateFormComplete: React.FC<BookingCreateFormCompleteProps> = (props) => {
  const { isDashboard = false } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const formRef = useRef<HTMLDivElement | null>(null);

  const history = useHistory();

  const { search } = useSearch(
    t.type({
      d2dNo: h.omittable(h.string()),
      id: h.omittable(h.string()),
    }),
  );

  return (
    <div ref={formRef}>
      {isDashboard ? (
        <SectionTitle>{strings.dashboardTitle}</SectionTitle>
      ) : (
        <div className={clsx(classes.pageTitleText, classes.textPrimary)}>{strings.title}</div>
      )}
      <Stepper
        baseUrl={isDashboard ? '/admin/widgets/BookingCreate' : '/utilities/BookingInquiry/'}
        steps={steps}
        value="Complete"
      />
      <div className={classes.articleContainer}>
        <div className={classes.thanksTitle}>{strings.thanksTitle}</div>
        <div className={classes.article}>
          {strings.article!(search.d2dNo!, search.id!, isDashboard)}
        </div>
        <div>
          <Button
            text={isDashboard ? strings.goToInquiry : strings.backText}
            color="primary"
            className={classes.button}
            type="button"
            onClick={() => {
              const homeUrl = isDashboard
                ? url('/admin/booking/BookingInquiries')
                : url('/utilities/Dashboard');
              history.push(homeUrl);
            }}
          />
        </div>
      </div>
    </div>
  );
};
